// ============================================================
//  兑换累加「Quote/Redeem」计价契约的本地 mock 引擎（纯逻辑、可单测）
//  契约：.scratch/prize-redeem-escalation/03、ADR-014 决策 §4/§5、spec Quote 契约
//
//  在后端计价权威就位前，用本引擎按同一套计价函数（redeem-escalation.ts）
//  在 dev 下模拟服务端：Quote 逐生报价、Redeem 逐生扣款（复用现有批量
//  部分成功语义）。本模块只做"镜像计算"，不触碰真实记录——用内存态
//  (units/balances) 维持"已兑件数演进"，Quote 只读不落库。
//  后端就位后关闭 mock 即切真实接口；真实路径由 http 层直接转发，本模块
//  不参与任何真实扣款/落库，生产构建不注册 mock（见环境开关）。
// ============================================================

import { priceSequence, type PrizeEscalation } from "../utils/redeem-escalation";
import type {
  RedeemQuoteParams,
  RedeemQuoteRow,
  RedeemQuoteResult,
  StudentRedeemResult,
  BatchRedeemResult,
} from "../services/redeem-record.service";

/** mock 需要的奖品最小快照（pointRequired 基础价 + escalation 配置） */
export interface MockPrizeSnapshot {
  id: number;
  pointsRequired: number;
  escalation?: PrizeEscalation | null;
}

/** mock 需要的已选学生最小快照（余额来自学生档案 score） */
export interface MockStudentSnapshot {
  studentId: number;
  studentName: string;
  score: number;
}

/**
 * 本地 mock 计价引擎：镜像服务端 Quote/Redeem 的口径。
 *
 * 内部按 (student × prize) 维护"已兑件数"演进、按学生维护余额扣减；
 * 同一实例内多次报价/兑换自然衔接（刚兑完再报价从新档位起步）。
 * 纯类、无 Angular 依赖，可脱离 DI 直接实例化测试。
 */
export class RedeemQuoteMockEngine {
  /** `${studentId}:${prizeId}` → 已兑件数 c（派生的内存镜像） */
  private readonly units = new Map<string, number>();
  /** studentId → 余额覆盖（mock 扣款演进；未覆盖则取快照 score） */
  private readonly balances = new Map<number, number>();

  private key(studentId: number, prizeId: number): string {
    return `${studentId}:${prizeId}`;
  }

  /** 播种已兑件数（供测试/演示构造"两名学生已兑件数不同"的场景） */
  seedRedeemed(studentId: number, prizeId: number, count: number): void {
    this.units.set(this.key(studentId, prizeId), Math.max(0, Math.floor(count)));
  }

  /** 覆盖学生余额（随 mock 扣款演进；未覆盖则用快照 score） */
  overrideBalance(studentId: number, balance: number): void {
    this.balances.set(studentId, Math.max(0, Math.floor(balance)));
  }

  reset(): void {
    this.units.clear();
    this.balances.clear();
  }

  private redeemedUnits(studentId: number, prizeId: number): number {
    return this.units.get(this.key(studentId, prizeId)) ?? 0;
  }

  private balanceOf(studentId: number, serverScore: number): number {
    return this.balances.get(studentId) ?? serverScore;
  }

  /** 逐生报价：只读计算，不改任何状态 */
  quote(
    prize: MockPrizeSnapshot,
    students: MockStudentSnapshot[],
    params: RedeemQuoteParams,
  ): RedeemQuoteResult {
    const rows: RedeemQuoteRow[] = params.studentIds.map((studentId) => {
      const stu = students.find((s) => s.studentId === studentId);
      const c = this.redeemedUnits(studentId, prize.id);
      const unitPrices = priceSequence(
        prize.pointsRequired,
        prize.escalation ?? null,
        c,
        params.quantity,
      );
      const total = unitPrices.reduce((sum, p) => sum + p, 0);
      const balance = this.balanceOf(studentId, stu?.score ?? 0);
      return {
        studentId,
        studentName: stu?.studentName ?? `学生${studentId}`,
        redeemedUnits: c,
        unitPrices,
        total,
        balance,
        balanceAfter: balance - total,
        enough: balance >= total,
      };
    });
    return { results: rows };
  }

  /**
   * 批量兑换：同函数重算逐生扣款并更新内存态（余额扣减、已兑件数 +quantity）。
   * 沿用批量部分成功语义——积分不足者失败（不改状态）、其余成功，不整单回滚。
   */
  redeem(
    prize: MockPrizeSnapshot,
    students: MockStudentSnapshot[],
    params: RedeemQuoteParams,
  ): BatchRedeemResult {
    const results: StudentRedeemResult[] = [];
    let successCount = 0;
    let failCount = 0;

    for (const studentId of params.studentIds) {
      const stu = students.find((s) => s.studentId === studentId);
      const c = this.redeemedUnits(studentId, prize.id);
      const unitPrices = priceSequence(
        prize.pointsRequired,
        prize.escalation ?? null,
        c,
        params.quantity,
      );
      const total = unitPrices.reduce((sum, p) => sum + p, 0);
      const balance = this.balanceOf(studentId, stu?.score ?? 0);

      if (balance < total) {
        results.push({
          studentId,
          studentName: stu?.studentName ?? `学生${studentId}`,
          success: false,
          error: "积分不足",
          pointsBefore: balance,
        });
        failCount++;
        continue;
      }

      const balanceAfter = balance - total;
      this.balances.set(studentId, balanceAfter);
      this.units.set(this.key(studentId, prize.id), c + params.quantity);
      results.push({
        studentId,
        studentName: stu?.studentName ?? `学生${studentId}`,
        success: true,
        pointsBefore: balance,
        pointsAfter: balanceAfter,
      });
      successCount++;
    }

    return { results, successCount, failCount };
  }

  /**
   * 取消一条兑换的内存镜像（镜像服务端 `Redeem/Cancel`）：移除一条该生该奖品
   * status=1 记录 → 派生已兑件数 -`quantity`；退款 = 该记录的 `pointsSpent`
   * （本引擎不存每条成本，退款由调用方按被取消记录传入，与真实 Cancel 逐条退
   * 款一致），冲回该生余额覆盖。
   *
   * 页面不直接调用——dev 的兑换是沙箱（未落真实记录，历史页无从取消 mock 单据），
   * 本方法作为生命周期 seam：核验"取消一条高档兑换 → 档位回退、再报回低档"的派生
   * 语义（06），后端 Quote 就位后用同口径抽样。余额覆盖不存在时以 0 为基线冲回
   * （仅测试/演示路径；正常总是先 redeem 已有覆盖）。
   *
   * @returns 退款金额（= 传入 refund，便于断言）
   */
  cancel(
    studentId: number,
    prizeId: number,
    quantity: number,
    refund: number,
  ): number {
    const key = this.key(studentId, prizeId);
    const current = this.redeemedUnits(studentId, prizeId);
    const q = Number.isFinite(quantity) ? Math.max(0, Math.floor(quantity)) : 0;
    this.units.set(key, Math.max(0, current - q)); // 已取消不计入派生档位
    const r = Number.isFinite(refund) ? Math.max(0, Math.floor(refund)) : 0;
    const had = this.balances.get(studentId);
    this.balances.set(studentId, Math.max(0, (had ?? 0) + r));
    return r;
  }
}
